import { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { statsApi } from '../../services/api';

// Parse extracted colors from JSONB array
const parseExtractedColors = (extractedColors) => {
  if (!extractedColors) return [];
  try {
    const colors = Array.isArray(extractedColors) ? extractedColors : JSON.parse(extractedColors);
    return colors
      .filter(c => c && c.score >= 0.6)
      .map(c => ({
        name: c.name || c.color || 'Unknown',
        hex: c.hex || '#888888',
        score: c.score || 0,
      }))
      // Sort by score so index 0 is the same "primary" colour the matching ETL
      // uses (_get_color_family / _get_primary_hex_color both sort by score).
      // Without this the dashboard could flag a different colour than the one
      // actually driving IDENTICAL relationships.
      .sort((a, b) => b.score - a.score);
  } catch (e) {
    return [];
  }
};

// Parse extracted pattern from JSONB array
const parseExtractedPattern = (extractedPattern) => {
  if (!extractedPattern) return null;
  try {
    const patterns = Array.isArray(extractedPattern) ? extractedPattern : JSON.parse(extractedPattern);
    const pattern = patterns.find(p => p && p.score >= 0.6);
    if (!pattern) return null;
    return {
      name: pattern.pattern || pattern.name || 'Unknown',
      score: pattern.score || 0,
    };
  } catch (e) {
    return null;
  }
};

// Get subcategory from normalized_description
const getSubcategory = (normalizedDescription) => {
  if (!normalizedDescription) return null;
  try {
    const desc = typeof normalizedDescription === 'string'
      ? JSON.parse(normalizedDescription)
      : normalizedDescription;
    return desc?.attributes?.subcategory || null;
  } catch (e) {
    return null;
  }
};

// Parse extracted_category from JSONB (from image analysis)
const parseExtractedCategory = (extractedCategory) => {
  if (!extractedCategory) return null;
  try {
    const data = typeof extractedCategory === 'string'
      ? JSON.parse(extractedCategory)
      : extractedCategory;
    if (!data || !data.category) return null;
    return {
      category: data.category,
      confidence: data.confidence || 0,
      source: data.source || 'image',
    };
  } catch (e) {
    return null;
  }
};

// Determine which category source "won" (description takes priority, then image)
const getActiveCategorySource = (normalizedDescription, extractedCategory) => {
  const subcategory = getSubcategory(normalizedDescription);
  if (subcategory) return 'description';

  const imageCategory = parseExtractedCategory(extractedCategory);
  if (imageCategory && imageCategory.confidence >= 0.7) return 'image';

  return null;
};

// Resolve a color label to its taxonomy family via the API-provided map.
// Normalized colors are shade-level labels (pink_dust, pink_medium); the family
// (pink, blue, green) is the level at which a real disagreement exists.
const getColorFamily = (colorFamilies, colorName) => {
  if (!colorFamilies || !colorName) return null;
  return colorFamilies[colorName.toLowerCase()] || null;
};

// Check if primary extracted color disagrees with the normalized color.
//
// Compares FAMILIES, not shade labels. pink_dust vs pink_medium are both the
// pink family and are not a discrepancy — flagging them buried the genuine
// cross-family errors (e.g. green_medium vs blue_turquoise) under ~28k false
// positives. When either family is unknown we do not warn, since an unmapped
// label is a taxonomy gap rather than evidence of a mismatch.
const hasColorDiscrepancy = (extractedColors, normalizedColor, colorFamilies) => {
  if (!extractedColors?.length || !normalizedColor) return false;
  const extractedFamily = getColorFamily(colorFamilies, extractedColors[0]?.name);
  const normalizedFamily = getColorFamily(colorFamilies, normalizedColor);
  if (!extractedFamily || !normalizedFamily) return false;
  return extractedFamily !== normalizedFamily;
};

// ColorSwatch component
const ColorSwatch = ({ hex }) => (
  <Box
    sx={{
      width: 14,
      height: 14,
      borderRadius: '50%',
      backgroundColor: hex,
      border: '1px solid rgba(0,0,0,0.2)',
      flexShrink: 0,
    }}
  />
);

// Match type color configuration (same as RelationshipsDashboard)
const MATCH_TYPE_CONFIG = {
  manual_demo: { color: '#9C27B0', label: 'Manual', bgColor: '#F3E5F5' },
  gtin_direct: { color: '#4CAF50', label: 'GTIN Direct', bgColor: '#E8F5E9' },
  gtin_transitive: { color: '#FF9800', label: 'GTIN Transitive', bgColor: '#FFF3E0' },
  hub_transitive: { color: '#009688', label: 'Hub Transitive', bgColor: '#E0F2F1' },
  embedding: { color: '#2196F3', label: 'Embedding', bgColor: '#E3F2FD' },
};

// Helper to render a match's type chip with optional extra info
const MatchTypeChip = ({ match }) => {
  const matchType = match.match_type || (match.match_methods?.includes('gtin') ? 'gtin_direct' : 'embedding');
  const config = MATCH_TYPE_CONFIG[matchType] || MATCH_TYPE_CONFIG.embedding;
  let label = config.label;

  // Add extra info for special types
  if (matchType === 'gtin_direct' && match.gtin_count) {
    label = `${config.label} (${match.gtin_count})`;
  } else if (matchType === 'gtin_transitive' && match.hops) {
    label = `${config.label} (${match.hops}h)`;
  } else if (matchType === 'hub_transitive' && match.shared_hubs) {
    label = `${config.label} (${match.shared_hubs}h)`;
  }

  return (
    <Tooltip title={`${matchType}: ${((match.similarity || 0) * 100).toFixed(0)}% similarity`}>
      <Chip
        label={label}
        size="small"
        sx={{
          backgroundColor: config.bgColor,
          color: config.color,
          fontWeight: 500,
          fontSize: '0.65rem',
          height: 20,
          '& .MuiChip-label': { px: 0.75 },
        }}
      />
    </Tooltip>
  );
};

/**
 * ProductDetailCard - Shows a single product's full details
 */
function ProductDetailCard({ product, sizes, matches, loading, error }) {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!product) {
    return <Alert severity="info">No product data</Alert>;
  }

  return (
    <Box>
      {/* Product Header */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        {product.image_url && (
          <Box
            component="img"
            src={product.image_url}
            alt=""
            sx={{
              width: 120,
              height: 160,
              objectFit: 'cover',
              borderRadius: 1,
              bgcolor: 'grey.100',
              flexShrink: 0,
            }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary">
            {product.retailer_name || product.retailer_id}
          </Typography>
          <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
            {product.raw_product_name}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Raw Product ID: {product.raw_product_id}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Supplier Code: {product.raw_supplier_code || '-'}
          </Typography>
          <Tooltip title="The code the matcher compares for IDENTICAL relationships">
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Style Code: {product.extracted_style_code || '-'}
              {product.code_extraction_source && (
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                  · via {product.code_extraction_source}
                </Typography>
              )}
            </Typography>
          </Tooltip>
          {product.normalized_product_id && (
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Normalized ID: {product.normalized_product_id}
            </Typography>
          )}
          {product.product_page_url && (
            <Link
              href={product.product_page_url}
              target="_blank"
              rel="noopener noreferrer"
              variant="body2"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              View on site <OpenInNewIcon fontSize="small" />
            </Link>
          )}
        </Box>
      </Box>

      {/* Missing embedding warning */}
      {product.has_embedding === false && (
        <Alert
          severity="warning"
          icon={<WarningIcon fontSize="small" />}
          sx={{ mb: 2, py: 0.25, '& .MuiAlert-message': { fontSize: '0.8rem' } }}
        >
          No embedding — this product cannot be matched via AI similarity or promoted from GTIN legacy.
        </Alert>
      )}

      {/* Attributes */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Attributes
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 500, width: 100 }}>Brand</TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2">{product.normalized_brand || '-'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Raw: {product.raw_brand || '(empty)'}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 500 }}>Color</TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2">
                      {product.normalized_color || '-'}
                      {getColorFamily(product.color_families, product.normalized_color) && (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                          · family: {getColorFamily(product.color_families, product.normalized_color)}
                        </Typography>
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Raw: {product.raw_color || '(empty)'}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 500 }}>Category</TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2">{product.normalized_category || '-'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Raw: {product.raw_category || '(empty)'}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 500 }}>Pattern</TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2">{product.raw_pattern || '-'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      (AI-extracted patterns shown below)
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
              {product.raw_materials && (
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>Materials</TableCell>
                  <TableCell>
                    <Typography variant="body2">{product.raw_materials}</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* AI Extracted Features */}
      {(() => {
        const extractedColors = parseExtractedColors(product.extracted_colors);
        const extractedPattern = parseExtractedPattern(product.extracted_pattern);
        const imageCategory = parseExtractedCategory(product.extracted_category);
        const descriptionCategory = getSubcategory(product.normalized_description);
        const activeCategorySource = getActiveCategorySource(product.normalized_description, product.extracted_category);
        const colorFamilies = product.color_families;
        const colorDiscrepancy = hasColorDiscrepancy(extractedColors, product.normalized_color, colorFamilies);
        const normalizedFamily = getColorFamily(colorFamilies, product.normalized_color);

        if (!extractedColors.length && !extractedPattern && !imageCategory && !descriptionCategory) return null;

        return (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              AI Extracted
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
              <Table size="small">
                <TableBody>
                  {extractedColors.length > 0 && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500, width: 120 }}>Colors (image)</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                          {extractedColors.map((color, idx) => {
                            const family = getColorFamily(colorFamilies, color.name);
                            return (
                            <Tooltip
                              key={idx}
                              title={`${color.name}${family ? ` (family: ${family})` : ' (no family mapping)'}: ${(color.score * 100).toFixed(0)}% confidence`}
                            >
                              <Chip
                                size="small"
                                label={family ? `${color.name} · ${family}` : color.name}
                                icon={<ColorSwatch hex={color.hex} />}
                                variant={idx === 0 ? 'filled' : 'outlined'}
                                sx={{
                                  pl: 0.5,
                                  '& .MuiChip-icon': { ml: 0.5 },
                                  ...(idx === 0 && colorDiscrepancy && {
                                    borderColor: 'warning.main',
                                    borderWidth: 2,
                                    borderStyle: 'solid',
                                  }),
                                }}
                              />
                            </Tooltip>
                            );
                          })}
                          {colorDiscrepancy && (
                            <Tooltip title={`Color family mismatch: image-extracted "${extractedColors[0]?.name}" is family "${getColorFamily(colorFamilies, extractedColors[0]?.name)}", but normalized "${product.normalized_color}" is family "${normalizedFamily}"`}>
                              <WarningIcon sx={{ fontSize: 18, color: 'warning.main', ml: 0.5 }} />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                  {imageCategory && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Category (image)</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Chip
                            size="small"
                            label={imageCategory.category}
                            color="primary"
                            variant={activeCategorySource === 'image' ? 'filled' : 'outlined'}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {(imageCategory.confidence * 100).toFixed(0)}% confidence
                          </Typography>
                          {activeCategorySource === 'image' && (
                            <Chip size="small" label="active" color="success" variant="outlined" sx={{ height: 18, '& .MuiChip-label': { px: 0.5, fontSize: '0.6rem' } }} />
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                  {descriptionCategory && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Category (desc)</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Chip
                            size="small"
                            label={descriptionCategory}
                            color="secondary"
                            variant={activeCategorySource === 'description' ? 'filled' : 'outlined'}
                          />
                          {activeCategorySource === 'description' && (
                            <Chip size="small" label="active" color="success" variant="outlined" sx={{ height: 18, '& .MuiChip-label': { px: 0.5, fontSize: '0.6rem' } }} />
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                  {product.normalized_category && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Normalized Cat.</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Chip size="small" label={product.normalized_category} color="success" />
                          {activeCategorySource && (
                            <Typography variant="caption" color="text.secondary">
                              (from {activeCategorySource})
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                  {extractedPattern && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Pattern (image)</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Chip
                            size="small"
                            label={`${extractedPattern.name} (${(extractedPattern.score * 100).toFixed(0)}%)`}
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        );
      })()}

      {/* Sizes */}
      {sizes && sizes.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Sizes ({sizes.length})
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {sizes.map((size, idx) => (
              <Tooltip
                key={idx}
                title={size.gtin ? `GTIN: ${size.gtin}` : 'No GTIN'}
                arrow
              >
                <Chip
                  label={size.size_name}
                  size="small"
                  color={size.gtin ? 'success' : 'default'}
                  variant={size.availability === 'InStock' ? 'filled' : 'outlined'}
                />
              </Tooltip>
            ))}
          </Box>
        </Box>
      )}

      {/* Matches */}
      {matches && matches.length > 0 && (
        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            All Matches ({matches.length})
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell>Retailer</TableCell>
                  <TableCell align="center">Method</TableCell>
                  <TableCell align="right">Similarity</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {matches.map((match, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {match.image_url && (
                          <Box
                            component="img"
                            src={match.image_url}
                            alt=""
                            sx={{ width: 32, height: 40, objectFit: 'cover', borderRadius: 0.5 }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        )}
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                            {match.raw_product_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {match.raw_product_id}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>
                        {match.retailer_name || match.retailer_id}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <MatchTypeChip match={match} />
                    </TableCell>
                    <TableCell align="right">
                      {((match.similarity || 1) * 100).toFixed(0)}%
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {matches && matches.length === 0 && (
        <Alert severity="info" sx={{ mt: 1 }}>
          No matches found for this product
        </Alert>
      )}
    </Box>
  );
}

/**
 * PairSummary - How the two products relate: do their style codes agree, and
 * which edge (if any) links them.
 */
function PairSummary({ productA, productB, matchesA }) {
  if (!productA || !productB) return null;

  const keyA = productA.style_code_key;
  const keyB = productB.style_code_key;
  let codeChip;
  if (!keyA || !keyB) {
    codeChip = <Chip size="small" color="warning" label="Style code missing" />;
  } else if (keyA === keyB) {
    codeChip = <Chip size="small" color="success" label={`Style codes match: ${keyA}`} />;
  } else {
    codeChip = <Chip size="small" color="error" label={`Style codes differ: ${keyA} vs ${keyB}`} />;
  }

  const edge = matchesA?.find(m => m.id === productB.id);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
      {codeChip}
      {edge ? (
        <>
          <Typography variant="body2" color="text.secondary">Linked by</Typography>
          <MatchTypeChip match={edge} />
          <Typography variant="body2" color="text.secondary">
            {((edge.similarity || 1) * 100).toFixed(0)}%
          </Typography>
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">Not linked</Typography>
      )}
    </Box>
  );
}

/**
 * ProductPairDrawer - Shows two products side-by-side in a slide-out drawer
 */
export default function ProductPairDrawer({
  open,
  onClose,
  productAId,
  productBId,
  // Optional: pre-loaded basic product info for instant display
  productABasic,
  productBBasic,
}) {
  const [productAData, setProductAData] = useState(null);
  const [productBData, setProductBData] = useState(null);
  const [loadingA, setLoadingA] = useState(false);
  const [loadingB, setLoadingB] = useState(false);
  const [errorA, setErrorA] = useState(null);
  const [errorB, setErrorB] = useState(null);

  // Load product A details
  useEffect(() => {
    if (!open || !productAId) {
      setProductAData(null);
      return;
    }

    const loadProduct = async () => {
      setLoadingA(true);
      setErrorA(null);
      try {
        const data = await statsApi.getProductById(productAId);
        if (data.error) {
          setErrorA(data.error);
        } else {
          setProductAData(data);
        }
      } catch (err) {
        setErrorA(err.message);
      } finally {
        setLoadingA(false);
      }
    };

    loadProduct();
  }, [open, productAId]);

  // Load product B details
  useEffect(() => {
    if (!open || !productBId) {
      setProductBData(null);
      return;
    }

    const loadProduct = async () => {
      setLoadingB(true);
      setErrorB(null);
      try {
        const data = await statsApi.getProductById(productBId);
        if (data.error) {
          setErrorB(data.error);
        } else {
          setProductBData(data);
        }
      } catch (err) {
        setErrorB(err.message);
      } finally {
        setLoadingB(false);
      }
    };

    loadProduct();
  }, [open, productBId]);

  const handleClose = () => {
    onClose();
    // Reset state after drawer closes
    setTimeout(() => {
      setProductAData(null);
      setProductBData(null);
      setErrorA(null);
      setErrorB(null);
    }, 300);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: { width: { xs: '100%', md: '80%', lg: '70%' }, maxWidth: 1200 },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6">Product Comparison</Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <PairSummary
          productA={productAData?.product}
          productB={productBData?.product}
          matchesA={productAData?.matches}
        />

        {/* Content - Two columns */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
          {/* Product A */}
          <Box
            sx={{
              flex: 1,
              p: 2,
              borderRight: { md: 1 },
              borderBottom: { xs: 1, md: 0 },
              borderColor: 'divider',
              overflow: 'auto',
            }}
          >
            <ProductDetailCard
              product={productAData?.product || productABasic}
              sizes={productAData?.sizes}
              matches={productAData?.matches}
              loading={loadingA}
              error={errorA}
            />
          </Box>

          {/* Divider with similarity indicator could go here */}

          {/* Product B */}
          <Box
            sx={{
              flex: 1,
              p: 2,
              overflow: 'auto',
            }}
          >
            <ProductDetailCard
              product={productBData?.product || productBBasic}
              sizes={productBData?.sizes}
              matches={productBData?.matches}
              loading={loadingB}
              error={errorB}
            />
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
}
